import { IconProp } from '@fortawesome/fontawesome-svg-core';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Box, FormControl, Tooltip, Typography, useTheme } from '@mui/material';
import { StatusFilter } from 'app/shared/model/enumerations/status-filter.model';
import axios from 'axios';
import PropTypes from 'prop-types';
import React from 'react';
import { ClearIndicatorProps, DropdownIndicatorProps, components } from 'react-select';
import { AsyncPaginate } from 'react-select-async-paginate';

interface Props {
  sortQuery?: string;
  value: any;
  apiUrl: string;
  valueName?: string;
  isRequired?: boolean;
  searchName?: string;
  placeholder?: string;
  isOptionDisabled?: any;
  label?: string;
  filterStyle?: boolean;
  statusFilter?: boolean;
  searchRepresentative?: boolean;
  icon?: IconProp;
  isDisabled?: boolean;
  isClearable?: boolean;
  onChange: (value: any) => void;
  onKeyDown?: (event: React.KeyboardEvent<HTMLDivElement>) => void;
  displayFormat?: (option: any) => string;
  renderOption?: (option: any) => React.ReactNode;
  // Takes precedence over renderOption; receives react-select's meta so callers can render menu and value differently.
  formatOptionLabel?: (option: any, meta: { context: 'menu' | 'value' }) => React.ReactNode;
  autoFocus?: boolean;
  openMenuOnFocus?: boolean;
  defaultMenuIsOpen?: boolean;
  minWidth?: number | string;
  desktopMinWidth?: number | string;
  controlHeight?: number | string;
  controlFontSize?: number | string;
  menuPortalTarget?: HTMLElement | null;
}

const SelectPaginateFromPaginatedApi: React.FC<Props> = ({
  sortQuery,
  value,
  apiUrl,
  placeholder = '',
  onChange,
  valueName = 'name',
  searchName = 'search',
  searchRepresentative,
  isRequired = false,
  onKeyDown,
  isOptionDisabled,
  label,
  statusFilter = false,
  icon,
  isDisabled = false,
  isClearable = false,
  displayFormat,
  renderOption,
  formatOptionLabel,
  autoFocus,
  openMenuOnFocus,
  defaultMenuIsOpen,
  minWidth = '250px',
  desktopMinWidth = '300px',
  controlHeight,
  controlFontSize,
  menuPortalTarget,
}) => {
  const theme = useTheme();
  const [hasValue, setHasValue] = React.useState<boolean>(false);
  const hasSelectedValue = Boolean(value);
  const customStyles = {
    control: (provided: any) => ({
      ...provided,
      width: '100%',
      maxWidth: '100%',
      boxSizing: 'border-box',
      minWidth,
      borderRadius: '8px',
      backgroundColor: 'transparent',
      color: theme.palette.text.secondary,
      boxShadow: 'none',
      ...(controlFontSize ? { fontSize: controlFontSize } : {}),
      ...(controlHeight ? { height: controlHeight, minHeight: controlHeight, padding: '0 10px' } : { padding: '3px 10px' }),
      borderColor: '#808080',

      '&:hover': {
        borderColor: theme.palette.text.primary,
      },
      '&:focus': {
        borderColor: theme.palette.secondary.main,
      },
      '@media (min-width: 900px)': {
        minWidth: desktopMinWidth,
      },
    }),
    container: (provided: any) => ({
      ...provided,
      width: '100%',
      minWidth: 0,
      maxWidth: '100%',
    }),
    menu: (provided: any) => ({
      ...provided,
      color: theme.palette.text.primary,
      padding: '6px',
      backgroundColor: theme.palette.background.paper,
      backgroundImage: 'linear-gradient(rgba(255, 255, 255, 0.12), rgba(255, 255, 255, 0.12));',
      boxShadow: '0px 5px 5px -3px rgba(0,0,0,0.2), 0px 8px 10px 1px rgba(0,0,0,0.14), 0px 3px 14px 2px rgba(0,0,0,0.12)',
    }),
    menuList: (provided: any) => ({
      ...provided,
    }),
    menuPortal: (provided: any) => ({
      ...provided,
      zIndex: theme.zIndex.modal + 1,
    }),
    placeholder: (provided: any) => ({
      ...provided,
      color: '#757575',
      maxWidth: '100%',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      whiteSpace: 'nowrap',
      ...(controlFontSize ? { fontSize: controlFontSize } : {}),
    }),
    option: (provided: any, state: { isFocused: boolean; isSelected: boolean }) => ({
      ...provided,
      backgroundColor: state.isSelected ? theme.palette.action.selected : 'transparent',

      '&:active': {
        backgroundColor: theme.palette.action.selected,
      },

      '&:hover': {
        backgroundColor: theme.palette.action.hover,
      },
    }),
    input: (provided: any) => ({
      ...provided,
      color: theme.palette.text.primary,
      ...(controlFontSize ? { fontSize: controlFontSize } : {}),
    }),
    valueContainer: (provided: any) => ({
      ...provided,
      minWidth: 0,
      overflow: 'hidden',
      ...(controlHeight ? { height: controlHeight, paddingTop: 0, paddingBottom: 0 } : {}),
    }),
    indicatorsContainer: (provided: any) => ({
      ...provided,
      ...(controlHeight ? { height: controlHeight } : {}),
    }),
    singleValue: (provided: any) => ({
      ...provided,
      color: theme.palette.text.primary,
      maxWidth: 'calc(100% - 8px)',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      whiteSpace: 'nowrap',
      ...(controlFontSize ? { fontSize: controlFontSize } : {}),
    }),
    clearIndicator: (provided: any) => ({
      ...provided,
      alignItems: 'center',
      cursor: 'pointer',
      display: 'flex',
      justifyContent: 'center',
      padding: 0,
      width: 32,
    }),
    indicatorSeparator: (provided: any) => ({
      ...provided,
      display: isClearable && hasSelectedValue ? 'none' : provided.display,
    }),
  };

  const DropdownIndicator = (props: DropdownIndicatorProps) => {
    return (
      <components.DropdownIndicator {...props}>
        <FontAwesomeIcon icon={icon} />
      </components.DropdownIndicator>
    );
  };

  const ClearIndicator = (props: ClearIndicatorProps) => (
    <components.ClearIndicator {...props}>
      <Box
        component="div"
        sx={{
          alignItems: 'center',
          backgroundColor: theme.palette.action.hover,
          borderRadius: '50%',
          color: theme.palette.text.secondary,
          display: 'inline-flex',
          height: 22,
          justifyContent: 'center',
          minHeight: '22px !important',
          transition: theme.transitions.create(['background-color', 'color']),
          width: 22,
          '&:hover': {
            backgroundColor: theme.palette.action.selected,
            color: theme.palette.text.primary,
          },
        }}
      >
        <CloseRoundedIcon sx={{ color: 'inherit', fontSize: 16, marginRight: '0 !important', width: '16px !important' }} />
      </Box>
    </components.ClearIndicator>
  );

  const loadOptions = async (searchQuery: string, loadedOptions: any, { page }: { page: number }) => {
    try {
      const { data } = await axios.get(apiUrl, {
        params: {
          page: page - 1,
          size: 10,
          sort: sortQuery || valueName,
          [searchName]: searchQuery,
          searchRepresentative,
          cacheBuster: new Date().getTime(),
          ...(statusFilter && { status: StatusFilter.ALL }),
        },
      });

      const result = data.content || [];

      if (page === 1 && !searchQuery && !isRequired) {
        result.unshift({ id: null });
      }

      return {
        options: result,
        hasMore: !data.last,
        additional: {
          page: page + 1,
        },
      };
    } catch (error) {
      throw new Error('Falha ao carregar as opções: ' + error);
    }
  };

  const getOptionValue = (option: any) => option?.id;
  const getOptionLabel = (option: any) => {
    if (displayFormat) {
      return displayFormat(option);
    }
    return option?.[valueName];
  };

  const selectComponents: any = {
    ...(icon && !(isClearable && hasSelectedValue) ? { DropdownIndicator } : {}),
    ...(isClearable ? { ClearIndicator } : {}),
    ...(isClearable && hasSelectedValue ? { DropdownIndicator: null } : {}),
  };

  const handleChange = (selectedOption: any) => {
    onChange(selectedOption?.id ? selectedOption : null);
    setHasValue(!!selectedOption);
  };

  return (
    <Box>
      <FormControl sx={{ width: '100%' }}>
        {label && (
          <Typography color={theme.palette.text.primary} fontWeight="600" fontSize="16px" textAlign="left">
            {label}
          </Typography>
        )}
        <Tooltip title={hasSelectedValue ? getOptionLabel(value) : ''} enterDelay={500} placement="top" disableHoverListener={!hasSelectedValue}>
          <Box sx={{ width: '100%', minWidth: 0 }}>
            <AsyncPaginate
              key={apiUrl} // This will remount the component when apiUrl changes
              onKeyDown={onKeyDown}
              loadingMessage={() => 'Carregando...'}
              noOptionsMessage={() => 'Nenhum resultado encontrado'}
              className={(hasValue || hasSelectedValue) && value ? 'input-react-select-async-paginate-with-value' : 'input-react-select-async-paginate'}
              value={value || ''}
              loadOptions={loadOptions}
              getOptionValue={getOptionValue}
              getOptionLabel={getOptionLabel}
              formatOptionLabel={formatOptionLabel ?? renderOption}
              onChange={handleChange}
              isSearchable
              isClearable={isClearable}
              placeholder={placeholder}
              isOptionDisabled={isOptionDisabled}
              components={selectComponents}
              additional={{
                page: 1,
              }}
              isDisabled={isDisabled}
              styles={customStyles}
              menuPortalTarget={menuPortalTarget}
              menuPosition={menuPortalTarget ? 'fixed' : 'absolute'}
              autoFocus={autoFocus}
              openMenuOnFocus={openMenuOnFocus}
              defaultMenuIsOpen={defaultMenuIsOpen}
            />
          </Box>
        </Tooltip>
      </FormControl>
    </Box>
  );
};

SelectPaginateFromPaginatedApi.propTypes = {
  value: PropTypes.object,
  onChange: PropTypes.func,
};

export default SelectPaginateFromPaginatedApi;
